import { Fade, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, NavLink } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { getMemberAppLinkPendingCount } from '../../api/memberAppLink';
import useUserAuth from '../../utils/useUserAuth';
import { GUEST_RESTRICTED_PATHS } from '../../constants/constants';
import {
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  IconWrapper,
  NotificationDot,
  SubMenuNotificationDot,
  MenuLabelPaper,
  MenuLabelText,
  SubMenuContainer,
  SubMenuItem,
  SingleMenuItem,
  ProBadge,
  getNavLinkStyle,
  getIconStyle,
} from './LeftMenuStyled';


// 메뉴 이름 옆에 Beta 표시
const BETA_MENU_PATHS = ['/salarySettlement', '/salarySetting', '/unconfirmed-attendance'];

// 대메뉴 이름 → PAGE_VIEW 권한 코드 매핑
const MENU_PAGE_VIEW_MAP = {
  'Member management': 'MEMBER_PAGE_VIEW',
  'Managing Attend Hisyory': 'ATTEND_PAGE_VIEW',
  'Schedule management': 'SCHEDULE_PAGE_VIEW',
  'Membership management': 'TICKET_PAGE_VIEW',
  'Message management': 'COMMUNITY_PAGE_VIEW',
  'Employee management': 'EMPLOYEE_PAGE_VIEW',
  'Center management': 'CENTER_PAGE_VIEW',
  'Revenue Management': 'SALES_PAGE_VIEW',
};

// 소메뉴 경로 → 소메뉴 권한 코드 매핑
const SUBMENU_AUTH_MAP = {
  '/members': 'MEMBER_UNREGISTERED_VIEW',
  '/member': 'MEMBER_GENERAL_VIEW',
  '/corp': 'MEMBER_CORP_VIEW',
  '/temporary': 'MEMBER_TEMP_VIEW',
  '/member-app-link': 'MEMBER_GENERAL_VIEW',
  '/private': 'SCHEDULE_PRIVATE_VIEW',
  '/group': 'SCHEDULE_GROUP_VIEW',
  '/online': 'SCHEDULE_GOLF_MANAGE',
  '/ticket': 'TICKET_COURSE_VIEW',
  '/pass': 'TICKET_PASS_VIEW',
  '/trial': 'TICKET_TRIAL_VIEW',
  '/chat': 'COMMUNITY_CHAT_VIEW',
  '/notice': 'COMMUNITY_NOTICE_VIEW',
  '/event': 'COMMUNITY_EVENT_VIEW',
  '/banner': 'COMMUNITY_BANNER_VIEW',
  '/transfer': 'COMMUNITY_TRANSFER_VIEW',
  '/sms': 'COMMUNITY_SMS_VIEW',
  '/employee': 'EMPLOYEE_SETTING_VIEW',
  '/salary': 'EMPLOYEE_SALARY_VIEW',
  '/salarySettlement': 'EMPLOYEE_SETTLEMENT_VIEW',
  '/salarySetting': 'EMPLOYEE_SALARY_SETTING_VIEW',
  '/attendanceManagement': 'EMPLOYEE_ATTENDANCE_MGMT_VIEW',
  '/store': 'CENTER_STORE_VIEW',
  '/product': 'CENTER_PRODUCT_VIEW',
  '/locker': 'CENTER_LOCKER_VIEW',
  '/point-management': 'CENTER_POINT_VIEW',
  '/permission': 'CENTER_PERMISSION_VIEW',
};

const LeftMenu = ({ open, handleChange, expanded, data, setExpanded, unreadCount = 0 }) => {
  const { t } = useTranslation();
  const userMenus = useSelector(state => state.menu.menu);
  const userRole = useSelector(state => state.user.user.userRole);
  // 시설 제어 노출 — 기업회원(center) 단위 설정값. 로그인 응답으로 내려온다.
  const facilityControlYn = useSelector(state => state.user.user.facilityControlYn);
  // 골프 관리 노출 — 기업회원(center) 단위 설정값. 로그인 응답으로 내려온다.
  const golfYn = useSelector(state => state.user.user.golfYn);

  const isMenuExpanded = expanded === data.menuId;
  const hasExpandedSubMenu = data.subMenus.filter(a => a.menuId === expanded).length > 0;

  // 라이트버전(ROLE_GUEST): 커뮤니티 관리 메뉴는 백엔드 권한이 없어도 노출한다.
  // 클릭하면 해당 페이지에서 라이트버전 블러 안내(LightVersionOverlay)로 막힌다.
  const isGuestUser = userRole === 'ROLE_GUEST';
  const isGuestForceVisibleMenu = isGuestUser && data.name === 'Message management';

  // 라이트버전에서 실제 이용 가능한 경로인지 (블러 차단 목록에 없으면 이용 가능)
  const isLiteAccessiblePath = path =>
    !GUEST_RESTRICTED_PATHS.some(restricted => path?.trim().startsWith(restricted));

  // 라이트버전 사용자에게 이용 불가(블러 차단) 메뉴에 PRO 배지를 표시
  const renderProBadge = path => isGuestUser && !isLiteAccessiblePath(path) && <ProBadge>PRO</ProBadge>;

  // 페이지 조회 권한 체크
  const pageViewCode = MENU_PAGE_VIEW_MAP[data.name];
  const hasPageView = useUserAuth(pageViewCode || '');
  // PAGE_VIEW 매핑이 없는 메뉴(바디스위치, 시설제어)는 기존 로직 유지
  const needsPageViewCheck = !!pageViewCode;

  // 안 읽은 채팅 개수 배지 (99 초과는 99+)
  const badgeText = unreadCount > 99 ? '99+' : unreadCount;

  // 앱 연동 요청 대기 건수 배지 — 회원 관리 메뉴에서만 조회한다.
  const APP_LINK_PATH = '/member-app-link';
  const APP_LINK_POLL_MS = 60000;
  const appLinkBranchId = useSelector(state => state.user.user.branch?.branchId);
  const hasAppLinkMenu = data.subMenus.some(sub => sub.path?.trim() === APP_LINK_PATH);
  const canSeeAppLink = userRole === 'ROLE_SUPERADMIN' || userMenus.some(m => m.menuAuthCode === 'MEMBER_GENERAL_VIEW');
  const { data: appLinkPending } = useQuery({
    queryKey: ['memberAppLinkPendingCount', appLinkBranchId],
    queryFn: () => getMemberAppLinkPendingCount(appLinkBranchId),
    enabled: hasAppLinkMenu && canSeeAppLink && !!appLinkBranchId,
    refetchInterval: APP_LINK_POLL_MS,
  });
  const appLinkCount = appLinkPending?.count ?? 0;
  const appLinkBadgeText = appLinkCount > 99 ? '99+' : appLinkCount;

  // 골프 타석 관리(/online) — SUPERADMIN 항상, 매니저는 기업회원 골프 관리 설정이 켜진 경우만
  const GOLF_PATH = '/online';
  const canSeeGolf = userRole === 'ROLE_SUPERADMIN' || (userRole === 'ROLE_MANAGER' && golfYn === 'Y');

  // SUPERADMIN 전용 서브메뉴 경로
  const superAdminOnlySubPaths = ['/members', '/corp', '/event', '/banner'];

  // 백엔드 메뉴 등록 없이 역할 기반으로 강제 노출할 서브메뉴 경로
  const roleForceVisiblePaths = ['/unconfirmed-attendance'];
  const roleForceVisibleRoles = ['ROLE_SUPERADMIN', 'ROLE_OPERATOR', 'ROLE_MANAGER'];

  // 시설 제어 메뉴는 백엔드 동적 메뉴(userMenus)에 없는 프론트 전용 메뉴라
  // userMenus 기준으로 판단하면 안 보인다. 역할과 기업회원 설정값으로 직접 판단한다.
  const FACILITY_CONTROL_PATH = '/facility-control';
  const FACE_ACCESS_PATH = '/facility-control/face-access-group';
  const canManagerSeeFacilityControl = userRole === 'ROLE_MANAGER' && facilityControlYn === 'Y';

  const isFacilitySubMenuPath = path => [FACILITY_CONTROL_PATH, FACE_ACCESS_PATH].includes(path?.trim());

  const canSeeFacilitySubMenu = path => {
    if (userRole === 'ROLE_SUPERADMIN') {
      return true;
    }
    // 출입문 관리(안면 인식 그룹)도 지점 매니저가 써야 한다(#91 출입문 체계) — 시설 제어와 같은 규칙.
    if (isFacilitySubMenuPath(path)) {
      return canManagerSeeFacilityControl;
    }
    return false;
  };

  // 비활성 스타일
  const disabledStyle = {
    pointerEvents: 'none',
    cursor: 'default',
  };

  // 소메뉴 권한 체크 함수 (useUserAuth와 동일 로직, 렌더 함수 내에서 사용)
  const hasSubmenuAuth = path => {
    const code = SUBMENU_AUTH_MAP[path?.trim()];
    if (!code) return false;
    if (userRole === 'ROLE_SUPERADMIN') return true;
    return userMenus.some(m => m.menuAuthCode === code);
  };

  const renderSubMenu = (menu, index) => {
    // 골프 타석 관리 — 기업회원 골프 관리 설정 기준
    if (menu.path?.trim() === GOLF_PATH && !canSeeGolf) {
      return null;
    }
    // SUPERADMIN 전용 서브메뉴 체크 (미등록/기업회원)
    if (superAdminOnlySubPaths.includes(menu.path?.trim())) {
      if (userRole !== 'ROLE_SUPERADMIN') {
        return null;
      }
    }

    const submenuAuthCode = SUBMENU_AUTH_MAP[menu.path?.trim()];
    const hasAuth = hasSubmenuAuth(menu.path);
    const isVisible = isFacilitySubMenuPath(menu.path)
      ? canSeeFacilitySubMenu(menu.path)
      : submenuAuthCode
      ? true // 소메뉴 권한 매핑 있으면 항상 표시 (비활성화만)
      : roleForceVisiblePaths.includes(menu.path?.trim())
      ? roleForceVisibleRoles.includes(userRole)
      : userMenus.some(i => i.path?.trim() === menu.path?.trim());

    if (isVisible) {
      // 소메뉴 권한 없으면 비활성화 + 툴팁 (라이트버전 강제 노출 메뉴는 클릭 가능 — 페이지에서 블러로 막음)
      if (submenuAuthCode && !hasAuth && !isGuestForceVisibleMenu) {
        // 라이트버전 차단 메뉴는 자물쇠 대신 PRO 배지로 표시
        const proBadge = renderProBadge(menu.path);
        const isBetaLocked = BETA_MENU_PATHS.includes(menu.path);
        return (
          <Tooltip key={index} title="권한이 없습니다" placement="right" arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
            <SubMenuItem style={{ pointerEvents: 'auto', cursor: 'default' }}>
              {t(menu.name)}
              {isBetaLocked && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #4AB3BC, #3582C0)', borderRadius: '4px', padding: '1px 5px', lineHeight: '16px' }}>Beta</span>}
              {' '}{proBadge || '🔒'}
            </SubMenuItem>
          </Tooltip>
        );
      }
      const isBeta = BETA_MENU_PATHS.includes(menu.path);
      return (
        <SubMenuItem key={index} component={NavLink} to={menu.path?.trim()} style={({ isActive }) => getNavLinkStyle(isActive)}>
          {t(menu.name)}
          {isBeta && <span style={{ marginLeft: '6px', fontSize: '10px', fontWeight: 700, color: '#fff', background: 'linear-gradient(135deg, #4AB3BC, #3582C0)', borderRadius: '4px', padding: '1px 5px', lineHeight: '16px' }}>Beta</span>}
          {renderProBadge(menu.path)}
          {menu.name === 'Chat' && unreadCount > 0 && <SubMenuNotificationDot>{badgeText}</SubMenuNotificationDot>}
          {menu.path?.trim() === APP_LINK_PATH && appLinkCount > 0 && (
            <SubMenuNotificationDot>{appLinkBadgeText}</SubMenuNotificationDot>
          )}
        </SubMenuItem>
      );
    }
    return null;
  };

  // 서브메뉴가 있는 경우 (Accordion)
  if (data.subMenus.length > 0) {
    // 시설 제어 아코디언은 userMenus 가 아닌 역할/지점 기준으로 노출 판단
    const isFacilityAccordion = data.subMenus.some(sub => isFacilitySubMenuPath(sub.path));
    const hasRoleForceVisibleSub =
      roleForceVisibleRoles.includes(userRole) &&
      data.subMenus.some(sub => roleForceVisiblePaths.includes(sub.path?.trim()));
    // 소메뉴 권한이 하나라도 있으면 대메뉴 표시
    const hasAnySubmenuAuth = data.subMenus.some(sub => hasSubmenuAuth(sub.path));

    // PAGE_VIEW 권한이 없으면 비활성화 (숨기지 않음) — 라이트버전 강제 노출 메뉴는 활성 유지
    const isDisabledByPageView = needsPageViewCheck && !hasPageView && !isGuestForceVisibleMenu;

    const isAccordionHidden = isGuestForceVisibleMenu
      ? false
      : isFacilityAccordion
      ? !data.subMenus.some(sub => canSeeFacilitySubMenu(sub.path))
      : needsPageViewCheck
      ? !hasAnySubmenuAuth && !hasPageView
      : !hasRoleForceVisibleSub && !userMenus.some(i => data.subMenus.some(j => i.path?.trim() === j.path?.trim()));

    const shouldHide = isAccordionHidden;

    const accordionContent = (
      <StyledAccordion
        disableGutters
        elevation={0}
        square
        hidden={shouldHide}
        expanded={isDisabledByPageView ? false : (isMenuExpanded || hasExpandedSubMenu)}
        onChange={isDisabledByPageView ? undefined : handleChange(data.menuId)}
        isExpanded={isDisabledByPageView ? false : isMenuExpanded}
        style={isDisabledByPageView ? disabledStyle : undefined}>
        <StyledAccordionSummary aria-controls="panel1d-content" id="panel1d-header">
          <IconWrapper>
            <data.icon active={isDisabledByPageView ? false : isMenuExpanded} />
            {data.name === 'Message management' && unreadCount > 0 && <NotificationDot>{badgeText}</NotificationDot>}
            {hasAppLinkMenu && appLinkCount > 0 && <NotificationDot>{appLinkBadgeText}</NotificationDot>}
          </IconWrapper>
          <Fade in={open}>
            <MenuLabelPaper>
              <MenuLabelText>{t(data?.name)}{isDisabledByPageView && ' 🔒'}</MenuLabelText>
            </MenuLabelPaper>
          </Fade>
        </StyledAccordionSummary>
        {open && !isDisabledByPageView && (
          <StyledAccordionDetails>
            <SubMenuContainer>{data.subMenus?.map((sub, index) => renderSubMenu(sub, index))}</SubMenuContainer>
          </StyledAccordionDetails>
        )}
      </StyledAccordion>
    );

    if (isDisabledByPageView) {
      return (
        <Tooltip title="권한이 없습니다" placement="right" arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
          <div>{accordionContent}</div>
        </Tooltip>
      );
    }

    return accordionContent;
  }

  // 서브메뉴가 없는 단일 메뉴
  const superAdminOnlyPaths = ['/facility-control', '/facility-control/face-access-group', '/app-version'];
  const isSuperAdminOnly = superAdminOnlyPaths.includes(data.path?.trim());

  if (isSuperAdminOnly) {
    const isFacilityControl = data.path?.trim() === '/facility-control';
    const isAllowedManager = isFacilityControl && canManagerSeeFacilityControl;
    if (userRole !== 'ROLE_SUPERADMIN' && !isAllowedManager) {
      return null;
    }
  }

  // PAGE_VIEW 권한 체크 (단일 메뉴)
  const isDisabledByPageView = needsPageViewCheck && !hasPageView;

  // 기존 SELECT 체크 — PAGE_VIEW 대상이면 비활성화로 대체, 아니면 기존 로직
  if (!isSuperAdminOnly && !needsPageViewCheck) {
    if (!userMenus.some(i => i.path?.trim() === data.path?.trim() && i.menuAuthCode === 'SELECT')) {
      return null;
    }
  }

  const singleMenuContent = (
    <SingleMenuItem
      onClick={isDisabledByPageView ? undefined : () => setExpanded(data.menuId)}
      component={isDisabledByPageView ? 'div' : Link}
      to={isDisabledByPageView ? undefined : data.path?.trim()}
      isExpanded={isDisabledByPageView ? false : isMenuExpanded}
      style={isDisabledByPageView ? disabledStyle : undefined}>
      <data.icon style={getIconStyle(isDisabledByPageView ? false : isMenuExpanded)} />
      <Fade in={open}>
        <MenuLabelPaper>
          <MenuLabelText>
            {t(data.name)}
            {isDisabledByPageView && ' 🔒'}
            {renderProBadge(data.path)}
          </MenuLabelText>
        </MenuLabelPaper>
      </Fade>
    </SingleMenuItem>
  );

  if (isDisabledByPageView) {
    return (
      <Tooltip title="권한이 없습니다" placement="right" arrow componentsProps={{ tooltip: { sx: { fontSize: '13px' } } }}>
        <div>{singleMenuContent}</div>
      </Tooltip>
    );
  }

  return singleMenuContent;
};

export default LeftMenu;
